import { useCallback, useState } from 'react'
import { useDropzone, FileRejection } from 'react-dropzone'
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE, validateFileSize } from '../../../utils/validation/fileValidation'
import { MAX_MENU_PAGES } from '../../../services/ai/menuImages'

interface FileUploadProps {
  onFileUpload: (files: File[]) => void
  className?: string
}

export const MAX_FILES = 5

function rejectionMessage(rejections: FileRejection[]): string {
  const codes = rejections.flatMap((r) => r.errors.map((e) => e.code))
  if (codes.includes('too-many-files')) return `Please upload ${MAX_FILES} files or fewer at a time.`
  if (codes.includes('file-invalid-type')) return 'Please upload a PDF, JPG, PNG or WebP file.'
  if (codes.includes('file-too-large')) return 'File size exceeds limit (10MB for PDFs, 5MB for images).'
  return 'That file could not be uploaded.'
}

export const FileUpload = ({ onFileUpload, className }: FileUploadProps) => {
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setError(null)

      if (fileRejections.length > 0) {
        setError(rejectionMessage(fileRejections))
        return
      }

      for (const file of acceptedFiles) {
        const result = validateFileSize(file)
        if (!result.isValid) {
          setError(result.error ?? 'File size exceeds limit')
          return
        }
      }

      if (acceptedFiles.length > 0) {
        onFileUpload(acceptedFiles)
      }
    },
    [onFileUpload]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: Object.fromEntries(Object.entries(ACCEPTED_FILE_TYPES).map(([type, exts]) => [type, [...exts]])),
    maxFiles: MAX_FILES,
    maxSize: MAX_FILE_SIZE.pdf,
    multiple: true,
  })

  return (
    <div
      {...getRootProps()}
      className={`cursor-pointer transition-all duration-200 ${className || ''}
        ${isDragActive ? 'scale-105' : ''}`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center">
        <svg className="w-8 h-8 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <h3 className="text-white text-lg font-medium mb-2">Upload your menu</h3>
        <p className="text-gray-300 text-sm mb-6">PDF, JPG, PNG or WebP. Up to 10MB per PDF, 5MB per image.</p>

        {isDragActive ? (
          <p className="text-blue-400 font-medium">Drop the files here...</p>
        ) : error ? (
          <div>
            <p className="text-red-400 font-medium mb-2">{error}</p>
            <p className="text-sm text-gray-300">Please try again with a valid file</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-300 text-sm mb-2">Drag & drop your menu files here</p>
            <p className="text-xs text-gray-400">
              or click to browse. Up to {MAX_FILES} files and {MAX_MENU_PAGES} pages in total.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
